import React, { useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Text,
    Flex,
    Heading,
    Header,
    Content,
    ButtonGroup,
    Button,
    ActionButton,
    View,
    Item,
    TabList,
    TabPanels,
    Divider,
    Dialog,
    DialogTrigger,
    ContextualHelp,
} from '@adobe/react-spectrum';
import './Deck.css';
import { DeepLinkTabs } from '../../utils/deepLink/DeepLinkTabs';
import { useDeepLinkSegment } from '../../utils/deepLink/useDeepLinkSegment';

import { REPORT_CARD_GRADE_CODES, getGrades, calcColorGenerically } from '../../data/ScoreDisplayUtils';
import {
    WOTC_FLOORED_HEADING,
    WOTC_FLOORED_NOTE_LEAD,
    WOTC_FLOORED_NOTE_BODY,
    WOTC_FLOORED_MARKER,
} from '../../data/bracketFloorCopy';
import LinkOut from '@spectrum-icons/workflow/LinkOut';
import AddCircle from '@spectrum-icons/workflow/AddCircle';
import FlashOn from '@spectrum-icons/workflow/FlashOn';
import Beaker from '@spectrum-icons/workflow/Beaker';
import Play from '@spectrum-icons/workflow/Play';
import Bookmark from '@spectrum-icons/workflow/Bookmark';
import DataMapping from '@spectrum-icons/workflow/DataMapping';
import Gauge4 from '@spectrum-icons/workflow/Gauge4';
import GraphBarVertical from '@spectrum-icons/workflow/GraphBarVertical';
import ColorPalette from '@spectrum-icons/workflow/ColorPalette';
import HotFixes from '@spectrum-icons/workflow/HotFixes';
import Merge from '@spectrum-icons/workflow/Merge';
import Card from '@spectrum-icons/workflow/Card';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { setAppRoute } from '../../data/redux/slices/appSlice';
import { Helmet } from 'react-helmet';
import { CombosPane } from './panes/CombosPane';
import { PowerLevelPane } from './panes/PowerLevelPane';
import { CardsPane } from './panes/CardsPane';
import { SaltPane } from './panes/SaltPane';
import { SynergyPane } from './panes/SynergyPane';
import isMobile from 'ismobilejs';
import { ManabasePane } from './panes/ManabasePane';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { NitroAd } from '../../ads/NitroAd';
import { SubmitForm } from '../submitform/SubmitForm';
import { UpgradelizerPane } from './panes/UpgradelizerPane';
import { ArchetypesPane } from './panes/ArchetypesPane';
import { BracketsPane } from './panes/BracketsPane';
import { PrintDialog } from '../widgets/printDialog/PrintDialog';
import { ManuelDialog } from '../widgets/manuelDialog/ManuelDialog';
import { fetchAuthorDetails } from '../../data/redux/slices/authorDetailsSlice';
import { fetchCommanderDetails } from '../../data/redux/slices/commanderDetailsSlice';
import { fetchUserDetails } from '../../data/redux/slices/userDetailsSlice';
import { GenericLoadingDialog } from '../genericLoadingDialog/GenericLoadingDialog';
import { useOpenCardInfo } from '../common/cardInfoDialog/useOpenCardInfo';
import { AvatarImage } from '../avatarImage/AvatarImage';
import { BreadCrumbShareButton } from '../breadcrumbs/buttons/BreadCrumbShareButton';
import { BreadCrumbRefreshButton } from '../breadcrumbs/buttons/BreadCrumbRefreshButton';
import { BreadCrumbAuthorLinkButtons } from '../breadcrumbs/buttons/BreadCrumbAuthorProfileLinkButton';
import { getDeckSourceName } from '../../utils/deckImportUrl';
import { AuthorAvatar } from '../avatarImage/AuthorAvatar';
import { BreadCrumbDeckLinkButton } from '../breadcrumbs/buttons/BreadCrumbDeckLinkButton';
import { NavLink } from '../common/NavLink';
import { doRefresh } from '../../data/redux/slices/importSlice';
import { BreadCrumbImportButton } from '../breadcrumbs/buttons/BreadCrumbImportButton';
import { BreadCrumbExportButton } from '../breadcrumbs/buttons/BreadCrumbExportButton';
import { BreadCrumbActionBar } from '../breadcrumbs/BreadCrumbActionBar';
import { BreadCrumbPlaytestButton } from '../breadcrumbs/buttons/BreadCrumbPlaytestButton.jsx';

export function Deck(props) {
    const deck = useSelector((state) => state.deck.deck);
    const isRefreshing = useSelector((state) => state.import.isRefreshing);
    const isImporting = useSelector((state) => state.import.isImporting);
    const isFetching = useSelector((state) => state.deck.isFetching);

    // hooks
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const mobile = isMobile(window.navigator).phone;
    const [manuelDialogOpen, setManuelDialogOpen] = useState(false);

    const grades = getGrades();
    const saltPassing =
        grades.salt.grade === REPORT_CARD_GRADE_CODES.A_PLUS ||
        grades.salt.grade === REPORT_CARD_GRADE_CODES.A ||
        grades.salt.grade === REPORT_CARD_GRADE_CODES.A_MINUS ||
        grades.salt.grade === REPORT_CARD_GRADE_CODES.B_PLUS ||
        grades.salt.grade === REPORT_CARD_GRADE_CODES.B;

    const getRemarks = () => {
        let saltQuote;
        let threatQuote;
        let comboQuote;

        switch (grades.salt.grade) {
            case REPORT_CARD_GRADE_CODES.A_PLUS:
                saltQuote = 'Aggressively salted';
                break;
            case REPORT_CARD_GRADE_CODES.A:
            case REPORT_CARD_GRADE_CODES.A_MINUS:
            case REPORT_CARD_GRADE_CODES.B_PLUS:
                saltQuote = 'Perfectly seasoned';
                break;
            case REPORT_CARD_GRADE_CODES.B:
                saltQuote = 'Nicely seasoned';
                break;
            case REPORT_CARD_GRADE_CODES.B_MINUS:
            case REPORT_CARD_GRADE_CODES.C_PLUS:
            case REPORT_CARD_GRADE_CODES.C:
            case REPORT_CARD_GRADE_CODES.C_MINUS:
                saltQuote = 'A bit underseasoned';
                break;
            case REPORT_CARD_GRADE_CODES.D:
            case REPORT_CARD_GRADE_CODES.D_PLUS:
                saltQuote = 'Rather bland';
                break;
            default:
                saltQuote = 'Completely devoid of salt';
                break;
        }

        switch (grades.threat.grade) {
            case REPORT_CARD_GRADE_CODES.A_PLUS:
                threatQuote = ` ${saltPassing ? ' and' : ' but otherwise'} expertly prepared`;
                break;
            case REPORT_CARD_GRADE_CODES.A:
            case REPORT_CARD_GRADE_CODES.A_MINUS:
            case REPORT_CARD_GRADE_CODES.B_PLUS:
            case REPORT_CARD_GRADE_CODES.B:
            case REPORT_CARD_GRADE_CODES.B_MINUS:
                threatQuote = `  ${saltPassing ? ' and' : ' but'}  well prepared`;
                break;
            case REPORT_CARD_GRADE_CODES.C_PLUS:
            case REPORT_CARD_GRADE_CODES.C:
                threatQuote = ` ${saltPassing ? ' yet' : ' and'} a touch undercooked`;
                break;
            default:
                threatQuote = ` ${saltPassing ? ' yet somehow' : ' and'} sloppily plated`;
                break;
        }

        if (deck?.powerLevelRating >= 9) {
            comboQuote = `...a competitive dish.`;
        } else if (deck?.powerLevelRating >= 7) {
            comboQuote = `... a delightful repast.`;
        } else if (deck?.powerLevelRating >= 5) {
            comboQuote = '... excellent for dinner with friends';
        } else {
            comboQuote = '... perfect for the kitchen table';
        }

        if (deck?.details?.manabase?.details?.lowLandCountFloorExceeded) {
            return `"Highly delicious, yet has the nutritional value of styrofoam."`;
        }

        if (deck?.powerLevelRating > 2) {
            return `"${saltQuote}${threatQuote}${comboQuote}"`;
        } else {
            return `"I ordered cupcakes... and I received a salad."`;
        }
    };

    const avatarUrl = deck?.author?.profileImageUrl;
    const title = deck?.title;
    const author = deck?.perks?.profileName || deck?.author?.username;
    let commanderList = deck?.commanders;
    if (!Array.isArray(commanderList)) {
        commanderList = [deck?.commanders];
    }
    let commanders = ``;
    commanderList.forEach((commander) => {
        if (commanders === ``) {
            commanders = commander;
        } else {
            commanders = `${commanders}\n${commander}`;
        }
    });

    const authorUrlButtonDisabled = !deck?.author?.url;

    const generatePowerLevelComponent = () => {
        let score = 0;
        if (deck?.powerLevelDisplayValue) {
            score = deck?.powerLevelDisplayValue;
        }

        const segments = [];
        for (let i = 1; i <= 10; i++) {
            const isActive = i === score;
            const isPast = i < score;
            const isFuture = i > score;
            segments.push({ id: i, value: i, isActive, isPast, isFuture });
        }

        const absoluteScore = deck?.powerLevelRating;

        return (
            <div className="deckPowerMeter">
                <div className="deckPowerMeterLabel">
                    <span className="deckPowerMeterLabelBar" />
                    <span className="deckPowerMeterLabelText">Practical Power Level</span>
                    <span className="deckPowerMeterLabelBar" />
                </div>
                <div className="deckPowerMeterBar">
                    {segments.map((seg) => {
                        // Classic "traffic" ramp — green at 1, yellow mid,
                        // orange-red at 8. Hue sweeps from ~105 down to ~15
                        // over values 1-8, then continues into hot ember
                        // territory for cEDH cells (9, 10) when the deck
                        // actually reaches that tier. Unreached cEDH cells
                        // stay dim via `.is-future` in CSS.
                        const ramped = Math.min(seg.value, 8);
                        const baseHue = 108 - (ramped - 1) * 13;
                        const isCedh = seg.value >= 9;
                        const cedhHue = 8 - (seg.value - 9) * 3;
                        const hue = isCedh ? cedhHue : baseHue;
                        const pastBg = `linear-gradient(180deg, hsl(${hue}, 70%, 56%) 0%, hsl(${hue}, 62%, 44%) 100%)`;
                        const activeBg = `linear-gradient(180deg, hsl(${hue}, 95%, 60%) 0%, hsl(${hue}, 90%, 42%) 100%)`;
                        let style = {};
                        if (seg.isPast) {
                            style = { background: pastBg };
                        } else if (seg.isActive) {
                            style = { background: activeBg };
                        }
                        // cEDH segments (9, 10) render as dim ember-brown cells
                        // with their number visible in the dark zone — matches
                        // the "cEDH ▸ 10" label below the bar.
                        return (
                            <div
                                key={`pow_seg_${seg.id}`}
                                className={
                                    'deckPowerSegment' +
                                    (seg.isActive ? ' is-active' : '') +
                                    (seg.isPast ? ' is-past' : '') +
                                    (seg.isFuture ? ' is-future' : '') +
                                    (isCedh ? ' deckPowerSegment--cedh' : '')
                                }
                                style={style}
                            >
                                <span className="deckPowerSegmentTick">{seg.value}</span>
                                {(seg.isActive || (isCedh && seg.isFuture)) && (
                                    <span className="deckPowerSegmentValue">{seg.value}</span>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="deckPowerMeterScale">
                    <span>&nbsp;</span>
                    <span className="deckPowerMeterScaleCedh">cEDH ▸ 10</span>
                </div>
                <div className="deckPowerMeterAbsolute">
                    <span className="deckPowerMeterAbsoluteLabel">Absolute score</span>
                    <span className="deckPowerMeterAbsoluteValue">{absoluteScore}</span>
                </div>
            </div>
        );
    };

    let header = document.getElementById('headerContainer');
    let preview = document.getElementById('previewDialog');

    const updateContentHeight = () => {
        const headerHeight = header.clientHeight;
        const previewHeight = preview.clientHeight;

        const previewScrollContainer = document.getElementById('previewScrollContainer');
        previewScrollContainer.style.setProperty(`max-height`, `${previewHeight - headerHeight - 70}px`);
    };

    const setAwaitTimer = () => {
        header = document.getElementById('headerContainer');
        preview = document.getElementById('previewDialog');

        if (!header || !preview) {
            setTimeout(() => {
                setAwaitTimer();
            }, 500);
        } else {
            updateContentHeight();
        }
    };

    setAwaitTimer();

    const openCardInfo = useOpenCardInfo();
    const [, setTab] = useDeepLinkSegment('powerLevel');

    const handleTabSelectionChange = (evn) => {
        setTab(evn);
        logEvent(getAnalytics(), `preview_setTab_${evn.toUpperCase()}`);

        const tabList = document.getElementById('tabPanel');
        const tabListTop = tabList?.getBoundingClientRect().top;

        if (mobile && tabListTop != null) {
            window.scrollBy({ top: tabListTop, left: 0, behavior: 'smooth' });
        }
    };

    const handleCommanderLinkClick = () => {
        // if (!props?.disableNavChanges) {
        dispatch(fetchCommanderDetails(deck?.commanderIndexId));
        dispatch(setAppRoute({ route: `/commanders/id/${encodeURIComponent(deck?.commanderIndexId)}` }));
        // }
    };

    const handleAuthorClick = () => {
        if (deck?.perks?.profileName) {
            dispatch(fetchUserDetails(deck.perks.profileName));
            dispatch(setAppRoute({ route: `/user/id/${encodeURIComponent(deck.perks.profileName)}` }));
        } else {
            if (deck?.authorIndexId) {
                dispatch(fetchAuthorDetails(deck?.authorIndexId));
                dispatch(setAppRoute({ route: `/authors/id/${encodeURIComponent(deck?.authorIndexId)}` }));
            }
        }
    };

    const handleChangeLogClick = () => {
        logEvent(getAnalytics(), 'preview_changelog', { url: deck?.deckUri });
        dispatch(setAppRoute({ route: `/changelog` }));
    };

    const handlePowerLevelsRouteClick = () => {
        logEvent(getAnalytics(), 'preview_powerLevels', { url: deck?.deckUri });
        // dispatch(setAppRoute({ route: `/changelog` }));
        // TODO: what the hell is happening here
        dispatch(setAppRoute({ route: `/powerlevels` }));
    };

    const allowManabase = !deck?.staleDeckData && deck?.details?.hasOwnProperty('manabase');

    if (deck && deck?.id) {
        if (window.location.pathname.includes(`/details/deck/`) && !window.location.pathname.includes(deck?.id)) {
            window.history.replaceState({}, null, `/details/deck/${deck.id}`);
        }
    }

    const ingestDateRaw = deck?.ingestDate?.ingestDate?.ingestDate || deck?.ingestDate?.ingestDate || deck?.ingestDate;
    const ingestDate = ingestDateRaw ? new Date(ingestDateRaw) : null;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const timeStamp =
        ingestDate && !isNaN(ingestDate.getTime())
            ? `${months[ingestDate.getMonth()]} ${ingestDate.getDate()}, ${ingestDate.getFullYear()}`
            : '';
    function getRandomInt(max) {
        return Math.floor(Math.random() * max);
    }
    const commentIndex = getRandomInt(deck?.manuel?.listOfResponses?.length);
    const comment = deck === null ? '' : deck?.manuel?.listOfResponses?.[commentIndex];

    const { pathname: deckPathname } = useLocation();
    const urlDeckId = decodeURIComponent(deckPathname.split('/details/deck/')[1]?.split('/')[0] || '');
    const idMatches = deck?.id && urlDeckId && deck.id === urlDeckId;
    const helmetName = idMatches ? `${deck?.title}` : 'Deck Details - Loading...';
    const helmetCanonical = `https://www.commandersalt.com/details/deck/${deck?.id}`;
    const helmetDescription = `Commandersalt.com analysis - ${deck?.title}`;
    const helmetTitle = idMatches ? `${deck?.title}` : 'Deck Details - Loading...';

    const displayProgressBar = isImporting || isRefreshing;
    const progressDialogHeader = isImporting ? 'Importing deck...' : 'Refreshing deck...';

    const getPrettyArchetypeLabel = (label) => {
        if (!label) return '';
        label = label.toLowerCase();
        // label = label === 'tutors' ? 'tutor' : label;
        // label = label === 'landbase' ? 'manabase' : label;
        // label = label === 'winConditions' ? 'win cons' : label;
        // label = label === 'fastmana' ? 'fast mana' : label;
        // label = label === 'combos' ? 'combo' : label;
        // label = label === 'answers' ? 'interaction' : label;
        // label = label === 'wincon' ? 'other' : label;

        label = label === 'spotremoval' ? 'spot removal' : label;
        label = label === 'cantrip' ? 'draw' : label;
        label = label === 'counters' ? 'counterspells' : label;
        label = label === 'plusoneplusonecounters' ? '+1/+1 COUNTERS' : label;
        label = label === 'extracombat' ? 'EXTRA COMBAT' : label;

        // console.log(label)
        label = label.toUpperCase();

        return label;
    };

    const disableAuthorButtons = isImporting || isRefreshing || !deck || !deck?.deckUri;
    const commanderCards = (deck?.commanderIds || []).map((id) => {
        let card = null;
        Object.keys(deck.cards).forEach((_id) => {
            if (deck.cards[_id].containerId === id && deck.cards[_id].isFrontFace) {
                card = deck.cards[_id];
            }
        });
        return card;
    });

    const hasBracketInfo = deck?.details?.hasOwnProperty('brackets');

    return (
        <Flex direction={'column'}>
            {deck && (
                <Helmet>
                    <title>{helmetName}</title>
                    <meta name="creator" content="Ben Rimbey" />
                    <link rel="canonical" href={helmetCanonical} />
                    <meta name="description" content={helmetDescription} />
                    <meta name="twitter:title" content={helmetTitle} />
                    <meta property="og:site_name" content="Commandersalt.com" />
                    <meta property="og:title" content={helmetTitle} />
                    <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                    <meta property="og:description" content={helmetDescription} />
                </Helmet>
            )}
            {displayProgressBar && (
                <GenericLoadingDialog eyebrow={isImporting ? 'Import' : 'Refresh'} title={progressDialogHeader} />
            )}

            <BreadCrumbs
                content={
                    <BreadCrumbActionBar
                        maxVisibleMobile={2}
                        secondaryPrimary={
                            <BreadCrumbRefreshButton
                                isDisabled={disableAuthorButtons}
                                url={deck?.deckUri || deck?.url}
                            />
                        }
                    >
                        {/* Sources without author attribution (e.g. commanderview)
                            have no profile to link — drop the button entirely once
                            the deck is loaded and we know the author URL is absent. */}
                        {(!deck || deck?.author?.url) && (
                            <BreadCrumbAuthorLinkButtons
                                isDisabled={disableAuthorButtons}
                                url={deck?.author?.url}
                                source={getDeckSourceName(deck?.source)}
                            />
                        )}
                        <BreadCrumbDeckLinkButton isDisabled={disableAuthorButtons} url={deck?.deckUri || deck?.url} />
                        <BreadCrumbShareButton
                            label={'Share deck'}
                            isDisabled={isImporting || isRefreshing || disableAuthorButtons}
                        />
                        <BreadCrumbExportButton isDisabled={isImporting || isRefreshing || disableAuthorButtons} />
                        <PrintDialog deckId={deck?.id} pageType={'preview'} name={deck?.name} />
                        <BreadCrumbPlaytestButton
                            deckId={deck?.id}
                            isDisabled={isImporting || isRefreshing || disableAuthorButtons}
                        />
                        <BreadCrumbImportButton isDisabled={isImporting || isRefreshing || disableAuthorButtons} />
                    </BreadCrumbActionBar>
                }
            />
            {isFetching && <GenericLoadingDialog />}

            <ManuelDialog isOpen={manuelDialogOpen} onClose={() => setManuelDialogOpen(false)} />

            {deck && (
                <Flex direction="column" margin={0}>
                    <>
                        <div className="deckHeroAssessmentRow panelGradient">
                            <div className="heroPanelWrap">
                                <div className="heroPanelCard">
                                    <div className="heroPanelImages">
                                        <div
                                            className="deckHeroAuthorBlock"
                                            onClick={handleAuthorClick}
                                            style={
                                                disableAuthorButtons ||
                                                (!deck?.authorIndexId && !deck?.perks?.profileName)
                                                    ? { cursor: 'default' }
                                                    : { cursor: 'pointer' }
                                            }
                                        >
                                            {deck?.perks?.avatarUri && deck?.perks?.avatarUri !== '' ? (
                                                <AvatarImage size={200} avatarUrl={deck.perks.avatarUri} />
                                            ) : (
                                                <AuthorAvatar
                                                    avatarUrl={avatarUrl}
                                                    alt={author}
                                                    className="heroPanelImage heroPanelImage--avatar"
                                                />
                                            )}
                                            <span className="deckHeroAuthorEyebrow">By</span>
                                            <span className="deckHeroAuthorName">{author || '—'}</span>
                                        </div>
                                    </div>
                                    <div className="heroPanelInfo">
                                        <div className="deckHeroName">{title}</div>
                                        <div className="deckHeroCommanderBlock">
                                            <div className="deckHeroSubLabel">Commander(s)</div>
                                            <div className="deckHeroCommanderGroup">
                                                <div className="deckHeroCommanderPills">
                                                    {commanderCards &&
                                                        commanderCards.length > 0 &&
                                                        commanderCards.map((card) =>
                                                            card ? (
                                                                <button
                                                                    type="button"
                                                                    key={`commander_pill_${card.id}`}
                                                                    className="deckHeroCommanderPill"
                                                                    onClick={() => openCardInfo(card)}
                                                                    title={`View card — ${card.name}`}
                                                                >
                                                                    {card.imageUri ? (
                                                                        <img
                                                                            src={card.imageUri}
                                                                            alt=""
                                                                            className="deckHeroCommanderPillThumb"
                                                                        />
                                                                    ) : (
                                                                        <span
                                                                            className="deckHeroCommanderPillThumb deckHeroCommanderPillThumb--placeholder"
                                                                            aria-hidden="true"
                                                                        />
                                                                    )}
                                                                    <span className="deckHeroCommanderPillName">
                                                                        {card.name}
                                                                    </span>
                                                                </button>
                                                            ) : null
                                                        )}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="deckHeroCommanderLink"
                                                    onClick={handleCommanderLinkClick}
                                                    disabled={props?.isDisabled}
                                                >
                                                    <span className="deckHeroCommanderLinkArrow" aria-hidden="true">
                                                        →
                                                    </span>
                                                    Open commander page
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="deckAssessmentWrap">
                                <div className="deckAssessmentCard">
                                    <div className="deckAssessmentInner">
                                        {/*<span className="deckChefsLabel">Our chef's remarks</span>*/}
                                        {/*<div className="deckChefsQuote">{getRemarks().replace(/^"|"$/g, '')}</div>*/}
                                        <div
                                            onClick={() => handleTabSelectionChange('powerLevel')}
                                            className={'reportCardItemWrapper'}
                                        >
                                            {generatePowerLevelComponent()}
                                        </div>
                                        <div className="deckAssessmentFooterLinks">
                                            <span
                                                className="deckAssessmentFooterLink"
                                                onClick={handlePowerLevelsRouteClick}
                                            >
                                                What does this mean?
                                            </span>
                                            <span className="deckAssessmentFooterSep">·</span>
                                            <span className="deckAssessmentFooterLink" onClick={handleChangeLogClick}>
                                                Has your score changed?
                                            </span>
                                        </div>
                                        {hasBracketInfo && (
                                            <div
                                                onClick={() => handleTabSelectionChange('brackets')}
                                                className={'reportCardItemWrapper'}
                                            >
                                                <Flex
                                                    direction="column"
                                                    alignItems={'center'}
                                                    rowGap={12}
                                                    marginTop={10}
                                                >
                                                    <span className="deckAssessmentSectionTitle">Bracket Rating</span>
                                                    <div className="deckBracketFrame">
                                                        <span className="deckBracketCap">[</span>
                                                        <div className="deckBracketGroup">
                                                            <div className="deckBracketTile">
                                                                <span className="deckBracketTileLabel">Baseline</span>
                                                                <span
                                                                    className="deckBracketTileValue"
                                                                    style={{
                                                                        color: calcColorGenerically(
                                                                            deck.details.brackets.wotcBracket,
                                                                            5
                                                                        ),
                                                                    }}
                                                                >
                                                                    {deck.details.brackets.wotcBracket}
                                                                    {deck.details.brackets.wotcBracketFloored && (
                                                                        <>
                                                                            <span className="deckBracketTileFloor">
                                                                                {WOTC_FLOORED_MARKER}
                                                                            </span>
                                                                            <ContextualHelp variant="info">
                                                                                <Heading>{WOTC_FLOORED_HEADING}</Heading>
                                                                                <Content>
                                                                                    <Text>
                                                                                        {WOTC_FLOORED_NOTE_LEAD}
                                                                                        <p />
                                                                                        {WOTC_FLOORED_NOTE_BODY}
                                                                                    </Text>
                                                                                </Content>
                                                                            </ContextualHelp>
                                                                        </>
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <div className="deckBracketTile">
                                                                <span className="deckBracketTileLabel">Realistic</span>
                                                                <span
                                                                    className="deckBracketTileValue"
                                                                    style={{
                                                                        color: calcColorGenerically(
                                                                            deck.details.brackets.csBracket,
                                                                            5
                                                                        ),
                                                                    }}
                                                                >
                                                                    {deck.details.brackets.csBracket}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <span className="deckBracketCap">]</span>
                                                    </div>
                                                </Flex>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <NitroAd id="deck" />
                        <Flex
                            direction="row"
                            justifyContent={'space-between'}
                            columnGap={20}
                            marginTop={10}
                            marginStart={0}
                            marginEnd={0}
                            marginBottom={0}
                            wrap
                        >
                            {/*{isRefreshing ? (*/}
                            {/*    <div style={{ marginTop: '5px' }}>*/}
                            {/*        <ImportStatusBar paddingTop="10px" />*/}
                            {/*    </div>*/}
                            {/*) : (*/}

                            <div
                                style={{
                                    display: 'initial',
                                    flex: '3 1 350px',
                                    // marginBottom: '10px',
                                    width: 350,
                                    minWidth: 350,
                                    maxWidth: 500,
                                }}
                            >
                                <View
                                    flex={1}
                                    padding="size-200"
                                    minHeight="0px"
                                    UNSAFE_className={'stickySideBarContainer panelGradient'}
                                >
                                    <div className="deckSidePanelStack">
                                        <div className="reportCardHeader">
                                            <Text>REPORT CARD</Text>
                                        </div>
                                        <div className="deckReportGradeGrid">
                                            <div
                                                className="deckReportGradeTile"
                                                onClick={() => handleTabSelectionChange('salt')}
                                            >
                                                <span className="deckReportGradeLabel">Saltiness</span>
                                                <span className="deckReportGradeLetter">{getGrades().salt.grade}</span>
                                                <span
                                                    className="deckReportGradeValue"
                                                    style={{ color: getGrades().salt.color }}
                                                >
                                                    {parseFloat(deck.saltRating).toFixed(1)}
                                                    <ContextualHelp variant="help">
                                                        <Heading>Saltiness</Heading>
                                                        <Content>
                                                            <Text>
                                                                This is purely a measure of how emotionally distressing
                                                                your deck is to your opponents.
                                                                <p />
                                                                This score does not affect the power level score - that
                                                                is an entirely different set of criteria.
                                                            </Text>
                                                        </Content>
                                                    </ContextualHelp>
                                                </span>
                                            </div>
                                            <div
                                                className="deckReportGradeTile"
                                                onClick={() => handleTabSelectionChange('cards')}
                                            >
                                                <span className="deckReportGradeLabel">Interaction</span>
                                                <span className="deckReportGradeLetter">
                                                    {getGrades().threat.grade}
                                                </span>
                                                <span
                                                    className="deckReportGradeValue"
                                                    style={{ color: getGrades().threat.color }}
                                                >
                                                    {parseInt(deck?.details?.powerLevel?.scoring?.interaction?.score)}
                                                </span>
                                            </div>
                                            <div
                                                className="deckReportGradeTile"
                                                onClick={() => handleTabSelectionChange('powerLevel')}
                                            >
                                                <span className="deckReportGradeLabel">Wincons</span>
                                                <span className="deckReportGradeLetter">
                                                    {getGrades().wincons.grade}
                                                </span>
                                                <span
                                                    className="deckReportGradeValue"
                                                    style={{ color: getGrades().wincons.color }}
                                                >
                                                    {deck.comboRating}
                                                </span>
                                            </div>
                                            <div
                                                className="deckReportGradeTile"
                                                onClick={() => handleTabSelectionChange('synergy')}
                                            >
                                                <span className="deckReportGradeLabel">Synergy</span>
                                                <span className="deckReportGradeLetter">
                                                    {getGrades().synergy.grade}
                                                </span>
                                                <span
                                                    className="deckReportGradeValue"
                                                    style={{ color: getGrades().synergy.color }}
                                                >
                                                    {parseFloat(
                                                        deck?.details?.synergy?.scoring?.totalScore || 0
                                                    ).toFixed(1)}
                                                </span>
                                            </div>
                                        </div>
                                        <div
                                            className="deckFlavorTile"
                                            onClick={() => handleTabSelectionChange('archetypes')}
                                        >
                                            <span className="deckSidePanelModuleLabel">Flavor Profile</span>
                                            <img
                                                src="/resources/saltyHand.png"
                                                alt="flavor"
                                                className="deckFlavorIcon"
                                            />
                                            <div className="deckFlavorValue">
                                                {getPrettyArchetypeLabel(deck?.archetypeMajor)}
                                                <span className="deckFlavorDivider">/</span>
                                                {getPrettyArchetypeLabel(deck?.archetypeMinor)}
                                            </div>
                                        </div>
                                        {allowManabase && (
                                            <div
                                                className="deckNutritionTile"
                                                onClick={() => handleTabSelectionChange('manabase')}
                                            >
                                                <span className="deckNutritionHeader">Nutrition Facts</span>
                                                <div className="deckNutritionThickRule" />
                                                <div className="deckNutritionDailyValueRow">% Daily Value</div>
                                                <div className="deckNutritionHairRule" />
                                                <div className="deckNutritionRow">
                                                    <span>
                                                        Manafixing{' '}
                                                        {parseFloat(deck?.details?.manabase?.manaFixingScore).toFixed(
                                                            0
                                                        )}
                                                        g
                                                    </span>
                                                    <span className="deckNutritionRowPct">
                                                        {parseFloat(
                                                            deck?.details?.manabase?.percentages?.manaFixing
                                                        ).toFixed(0)}
                                                        %
                                                    </span>
                                                </div>
                                                <div className="deckNutritionHairRule" />
                                                <div className="deckNutritionRow">
                                                    <span>
                                                        Playing on curve{' '}
                                                        {parseFloat(
                                                            deck?.details?.manabase?.curveChart?.curveAppeal
                                                        ).toFixed(0)}
                                                        g
                                                    </span>
                                                    <span className="deckNutritionRowPct">
                                                        {parseFloat(
                                                            deck?.details?.manabase?.percentages?.curve
                                                        ).toFixed(0)}
                                                        %
                                                    </span>
                                                </div>
                                                <div className="deckNutritionHairRule" />
                                                <div className="deckNutritionRow">
                                                    <span>
                                                        Manabase Quality{' '}
                                                        {parseFloat(
                                                            deck?.details?.manabase?.curveChart?.quality
                                                        ).toFixed(0)}
                                                        g
                                                    </span>
                                                    <span className="deckNutritionRowPct">
                                                        {parseFloat(
                                                            deck?.details?.manabase?.curveChart?.quality
                                                        ).toFixed(0)}
                                                        %
                                                    </span>
                                                </div>
                                                {deck?.details?.manabase?.curveChart?.accelerationBonus > 0 && (
                                                    <>
                                                        <div className="deckNutritionHairRule" />
                                                        <div className="deckNutritionBonusRow">
                                                            <span>
                                                                [BONUS] Early game acceleration{' '}
                                                                {deck?.details?.manabase?.curveChart?.accelerationBonus}
                                                                g
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                                <div className="deckNutritionThickRule" />
                                                <div className="deckNutritionTotalRow">
                                                    <span>
                                                        Total Calories{' '}
                                                        {parseInt(
                                                            deck?.details?.manabase?.totalCalories || '0'
                                                        ).toFixed(0)}
                                                        g
                                                    </span>
                                                    <span>
                                                        {parseFloat(
                                                            deck?.details?.manabase?.percentages?.overall
                                                        ).toFixed(0)}
                                                        %
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        {deck?.manuel && (
                                            <div
                                                className="deckFieldReport deckFieldReport--clickable"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => {
                                                    logEvent(getAnalytics(), 'manuel_click', { url: deck?.deckUri });
                                                    setManuelDialogOpen(true);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        logEvent(getAnalytics(), 'manuel_click', {
                                                            url: deck?.deckUri,
                                                        });
                                                        setManuelDialogOpen(true);
                                                    }
                                                }}
                                            >
                                                <span className="deckFieldReportLabel">Field Report</span>
                                                <div className="deckFieldReportHeader">
                                                    <img
                                                        src="/resources/manuel_avatar.png"
                                                        alt="Manuel"
                                                        className="deckFieldReportAvatar"
                                                    />
                                                    <div className="deckFieldReportPersona">
                                                        <span className="deckFieldReportPersonaName">
                                                            Manuel, Nunchaku Specialist
                                                        </span>
                                                        <span className="deckFieldReportPersonaHandle">r/EDH</span>
                                                    </div>
                                                    {timeStamp && (
                                                        <span className="deckFieldReportDate">{timeStamp}</span>
                                                    )}
                                                </div>
                                                <div className="deckFieldReportStars" aria-label="1 out of 5 stars">
                                                    <span className="deckFieldReportStarFilled">★</span>
                                                    <span className="deckFieldReportStarEmpty">★</span>
                                                    <span className="deckFieldReportStarEmpty">★</span>
                                                    <span className="deckFieldReportStarEmpty">★</span>
                                                    <span className="deckFieldReportStarEmpty">★</span>
                                                </div>
                                                <div className="deckFieldReportQuote">{comment}</div>
                                                {deck?.manuel?.category && (
                                                    <span className="deckFieldReportCategory">
                                                        {deck.manuel.category}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </View>
                            </div>
                            <div
                                style={{
                                    display: 'initial',
                                    flex: '3 1 350px',
                                    // marginBottom: '10px',
                                    width: 350,
                                    minWidth: 350,
                                    // maxWidth: 500,
                                }}
                            >
                                <View flex={1} paddingTop="size-0" minHeight="0px" UNSAFE_className="panelGradient">
                                    <Flex direction={'column'}>
                                        <DeepLinkTabs
                                            defaultSelectedKey="powerLevel"
                                            isEmphasized
                                            UNSAFE_style={{ padding: 0 }}
                                            onSelectionChange={handleTabSelectionChange}
                                            id={'tabPanel'}
                                        >
                                            <TabList
                                                marginTop="20px"
                                                marginStart="10px"
                                                UNSAFE_className={'stickyTabBarContainer'}
                                            >
                                                <Item key="archetypes">
                                                    <HotFixes size={'XS'} />
                                                    Archetypes
                                                </Item>
                                                {hasBracketInfo && (
                                                    <Item key="brackets">
                                                        <Merge size={'XS'} />
                                                        Brackets
                                                    </Item>
                                                )}
                                                <Item key="cards">
                                                    <Card size={'XS'} />
                                                    Cards
                                                </Item>
                                                <Item key="combos">
                                                    <FlashOn size={'XS'} />
                                                    Combos
                                                </Item>
                                                <Item key="manabase">
                                                    <ColorPalette size={'XS'} />
                                                    Manabase
                                                </Item>
                                                <Item key="powerLevel">
                                                    <GraphBarVertical size={'XS'} />
                                                    Power Levels
                                                </Item>
                                                <Item key="salt">
                                                    <Beaker size={'XS'} />
                                                    Saltiness
                                                </Item>
                                                <Item key="synergy">
                                                    <DataMapping size={'XS'} />
                                                    Synergy
                                                </Item>
                                                <Item key="upgrades">
                                                    <Gauge4 size={'XS'} />
                                                    Upgrades
                                                </Item>
                                            </TabList>
                                            <TabPanels marginTop="10px" marginStart={0} marginEnd={5}>
                                                <Item key="powerLevel">
                                                    <PowerLevelPane />
                                                </Item>
                                                <Item key="upgrades">
                                                    <UpgradelizerPane />
                                                </Item>
                                                <Item key="manabase">
                                                    <ManabasePane />
                                                </Item>
                                                <Item key="salt">
                                                    <SaltPane />
                                                </Item>
                                                <Item key="archetypes">
                                                    <ArchetypesPane />
                                                </Item>
                                                {hasBracketInfo && (
                                                    <Item key="brackets">
                                                        <BracketsPane />
                                                    </Item>
                                                )}
                                                <Item key="synergy">
                                                    <SynergyPane />
                                                </Item>
                                                <Item key="combos">
                                                    <CombosPane />
                                                </Item>
                                                <Item key="cards">
                                                    <CardsPane />
                                                </Item>
                                            </TabPanels>
                                        </DeepLinkTabs>
                                    </Flex>
                                </View>
                            </div>
                        </Flex>
                    </>
                </Flex>
            )}
        </Flex>
    );
}
